import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useImperativeHandle, type ReactNode, type Ref } from 'react';
import { Controller, useForm, type Path } from 'react-hook-form';

import { applyServerErrors } from '@/api/serverErrors';
import { ContactFields } from '@/components/form/ContactFields';
import { GeographicScopeFields } from '@/components/form/GeographicScopeFields';
import { SocialLinksEditor } from '@/components/form/SocialLinksEditor';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { ORGANIZATION_TYPES } from '@/lib/organization';

import {
  profileFormSchema,
  type ProfileDirtyFields,
  type ProfileFormOutput,
  type ProfileFormValues,
} from './profileSchema';

/**
 * The server's field names to the form's, in page order.
 *
 * The server names each refused field by the last part of its path alone, so
 * the two contacts both arrive as "email", "phone" and "title" - and both are
 * sent to the primary contact's field here, because they cannot be told apart
 * until the structured error format lands. Wrong field, right words; the
 * README's server-error note records it so QA does not file it twice.
 */
const SERVER_FIELDS: Record<string, Path<ProfileFormValues>> = {
  name: 'name',
  organization_type: 'organization_type',
  description: 'description',
  website: 'website',
  geographic_scope: 'geographic_scope',
  geographic_states: 'geographic_states',
  scope_description: 'scope_description',
  social_links: 'social_links',
  email: 'contact_email',
  phone: 'contact_phone',
  title: 'contact_title',
};

/** What the page can do to the form from outside it. */
export interface ProfileFormHandle {
  /** Replaces every value and marks the form clean, so Save turns off again. */
  reset: (values: ProfileFormValues) => void;
  /**
   * Puts a refusal's field messages on their fields and focuses the first;
   * returns whatever concerned no field, for the page's form-level alert.
   */
  applyServerErrors: (error: unknown) => { remainder: string | null };
}

interface ProfileFormProps {
  defaultValues: ProfileFormValues;
  onSubmit: (values: ProfileFormOutput, dirtyFields: ProfileDirtyFields) => void;
  pending: boolean;
  /**
   * True when the organization is suspended or deactivated. Every control is
   * disabled and Save is not offered: the backend would refuse the write, and
   * saying so first is kinder than letting somebody type a paragraph and lose it.
   */
  readOnly?: boolean;
  /** The two image controls, which the page owns because they save on their own. */
  images?: ReactNode;
  ref?: Ref<ProfileFormHandle>;
}

function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" data-testid={testId} sx={{ p: 3 }}>
      <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>{children}</Box>
    </Paper>
  );
}

/**
 * The organization's own page, as its Super Admin edits it.
 *
 * Its own form rather than the staff form in a third mode: it has no web
 * handle, no status and no billing, and it has three things staff never see -
 * two images and the public contact. A shared form would be half hidden in each
 * of the two places it appeared.
 */
export function ProfileForm({
  defaultValues,
  onSubmit,
  pending,
  readOnly,
  images,
  ref,
}: ProfileFormProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setError,
    setFocus,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<ProfileFormValues, unknown, ProfileFormOutput>({
    resolver: zodResolver(profileFormSchema),
    defaultValues,
  });

  // Resetting and error placement are driven by the page: only it knows a
  // save came back or was refused, but only this form knows its fields. A 409
  // is the name clash, which lands whole on the Name field as it always has.
  useImperativeHandle(
    ref,
    () => ({
      reset: (values) => reset(values),
      applyServerErrors: (error) =>
        applyServerErrors(error, { setError, setFocus }, SERVER_FIELDS, {
          conflictField: 'name',
        }),
    }),
    [reset, setError, setFocus],
  );

  // The same flag the Save button reads, so the prompt and the button cannot
  // disagree about whether anything is unsaved.
  const unsaved = useUnsavedChanges(isDirty);

  return (
    <Box
      component="form"
      onSubmit={(event) =>
        void handleSubmit((values) =>
          // React Hook Form types an array field's flag as an array of flags,
          // which is more than any caller here needs to know.
          onSubmit(values, dirtyFields as ProfileDirtyFields),
        )(event)
      }
      sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 760 }}
      noValidate
      data-testid="profile-form"
    >
      <Section title="The organization" testId="profile-form-organization-section">
        {/* A server refusal sits here through setError until the name is
            edited, when re-validation replaces it - so it never lingers under
            a brand new name claiming that one is taken too. */}
        <Field label="Name" error={errors.name?.message} testId="profile-form-name">
          <TextField disabled={readOnly} {...register('name')} />
        </Field>

        {/* Controlled rather than registered. A select given a defaultValue
            reads it once, so a reset after a save would leave it showing the
            choice it started with. */}
        <Controller
          name="organization_type"
          control={control}
          render={({ field }) => (
            <Field
              label="Type"
              error={errors.organization_type?.message}
              testId="profile-form-type"
            >
              <TextField select {...field} value={field.value ?? ''} disabled={readOnly}>
                <MenuItem value="">Not set</MenuItem>
                {ORGANIZATION_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
          )}
        />

        <Field
          label="Description"
          hint="Shown on the organization's page in the app."
          error={errors.description?.message}
          testId="profile-form-description"
        >
          <TextField
            multiline
            minRows={3}
            disabled={readOnly}
            {...register('description')}
          />
        </Field>

        <Field
          label="Website"
          error={errors.website?.message}
          testId="profile-form-website"
        >
          <TextField
            placeholder="https://example.org"
            disabled={readOnly}
            {...register('website')}
          />
        </Field>
      </Section>

      {images && (
        <Section title="Logo and cover image" testId="profile-form-images-section">
          {images}
        </Section>
      )}

      <Section title="Where you work" testId="profile-form-scope-section">
        <GeographicScopeFields
          names={{
            scope: 'geographic_scope',
            states: 'geographic_states',
            note: 'scope_description',
          }}
          control={control}
          register={register}
          watch={watch}
          errors={errors}
          disabled={readOnly}
          testId="profile-form"
        />
      </Section>

      <Section title="Social links" testId="profile-form-social-section">
        <SocialLinksEditor
          name="social_links"
          control={control}
          errors={errors}
          disabled={readOnly}
          testId="profile-form-social-links"
        />
      </Section>

      <Section title="Primary contact" testId="profile-form-contact-section">
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
          Who Activist Technologies deals with. Not shown in the app.
        </Typography>

        <ContactFields
          names={{
            name: 'contact_name',
            title: 'contact_title',
            email: 'contact_email',
            phone: 'contact_phone',
          }}
          register={register}
          errors={errors}
          disabled={readOnly}
          testId="profile-form-contact"
        />
      </Section>

      <Section title="Public contact" testId="profile-form-public-contact-section">
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
          How supporters reach you. Shown in the app.
        </Typography>

        <ContactFields
          labelPrefix="Public"
          names={{
            name: 'public_contact_name',
            title: 'public_contact_title',
            email: 'public_contact_email',
            phone: 'public_contact_phone',
          }}
          register={register}
          errors={errors}
          disabled={readOnly}
          testId="profile-form-public-contact"
        />
      </Section>

      {/* No Save at all while the account is suspended: a disabled button
          invites somebody to keep trying it. The notice above the form says
          why. */}
      {!readOnly && (
        <Box>
          <Button
            type="submit"
            loading={pending}
            disabled={!isDirty}
            data-testid="profile-form-save-button"
          >
            Save changes
          </Button>
        </Box>
      )}

      <UnsavedChangesDialog {...unsaved} />
    </Box>
  );
}
